import React from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import {
  useQuest,
  usePromotion,
  useQuestTarget,
  usePurchaseChannels,
  useUpdateQuest,
  Store,
  ProductWithManufacturer,
  Category as CategoryType,
  Manufacturer as ManufacturerType,
  Keyword as KeywordType,
} from "@quest_editor/mocks";
import PurchaseContentSection from "./PurchaseContentSection";

const QuestEditPurchase = () => {
  const { t } = useTranslation("questEditor");
  const navigate = useNavigate();
  const { id: questId } = useParams<{ id: string }>();

  const [purchaseChannelIds, setPurchaseChannelIds] = React.useState<string[]>(
    [],
  );
  const [targetStoreRows, setTargetStoreRows] = React.useState<Store[]>([]);
  const [targetProductRows, setTargetProductRows] = React.useState<
    ProductWithManufacturer[]
  >([]);
  const [categoryRows, setCategoryRows] = React.useState<CategoryType[]>([]);
  const [manufacturerRows, setManufacturerRows] = React.useState<
    ManufacturerType[]
  >([]);
  const [keywordRows, setKeywordRows] = React.useState<KeywordType[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: questData } = useQuest(questId);
  const quest = questData?.quest;

  const { data: promotionData } = usePromotion(quest?.promotionId);
  const promotion = promotionData?.promotion;

  const { data: questTargetData } = useQuestTarget(questId);
  const questTarget = questTargetData?.questTarget;

  const { data: purchaseChannelsData } = usePurchaseChannels();
  const purchaseChannels = purchaseChannelsData?.purchaseChannels ?? [];
  const { updateQuest } = useUpdateQuest();

  const loadedQuestIdRef = React.useRef<string | undefined>();

  React.useEffect(() => {
    if (!questTarget || !questId) return;
    if (loadedQuestIdRef.current === questId) return;
    loadedQuestIdRef.current = questId;

    setPurchaseChannelIds(questTarget.purchaseChannels.map((pc) => pc.id));
    setTargetStoreRows(questTarget.stores);
    setTargetProductRows(questTarget.products);
    setCategoryRows(questTarget.categories);
    setManufacturerRows(questTarget.manufacturers);
    setKeywordRows(questTarget.keywords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, questTarget]);

  const save = async () => {
    if (!questId) return;
    await updateQuest({ id: questId });
  };

  const handleSaveAndBack = async () => {
    if (!promotion || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/promotion/${promotion.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndNext = async () => {
    if (!quest || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/quest/${quest.id}/edit/survey`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!quest || !promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Breadcrumb items={[{ label: promotion.name, to: `/quest_editor/promotion/${promotion.id}` }, { label: quest.name }]} />
          <Stack direction="row" spacing={1}>
            <PromotionTypeChip type={promotion.type} />
            <PromotionStatusChip status={promotion.status} />
          </Stack>
        </Stack>
      </Stack>

      <PurchaseContentSection
        purchaseChannels={purchaseChannels}
        purchaseChannelIds={purchaseChannelIds}
        setPurchaseChannelIds={setPurchaseChannelIds}
        targetStoreRows={targetStoreRows}
        setTargetStoreRows={setTargetStoreRows}
        targetProductRows={targetProductRows}
        setTargetProductRows={setTargetProductRows}
        categoryRows={categoryRows}
        setCategoryRows={setCategoryRows}
        manufacturerRows={manufacturerRows}
        setManufacturerRows={setManufacturerRows}
        keywordRows={keywordRows}
        setKeywordRows={setKeywordRows}
      />

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        alignItems="center"
        sx={{ position: "relative" }}
      >
        <BackButton
          component={Link}
          to={`/quest_editor/quest/${quest.id}`}
          sx={{ position: "absolute", left: 0 }}
        >
          {t("quest_edit_purchase.button.back")}
        </BackButton>

        <Stack direction="row" spacing={2}>
          <ActionButton onClick={handleSaveAndBack} disabled={isSubmitting}>
            {t("quest_edit_purchase.button.save_and_back")}
          </ActionButton>
          <ActionButton onClick={handleSaveAndNext} disabled={isSubmitting}>
            {t("quest_edit_purchase.button.save_and_goto_next")}
          </ActionButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default QuestEditPurchase;
